import type { Block } from './pages-api';
import { apiRequest } from '../lib/api-client';

/** Segments, campaigns and consent (context.md sections 45, 46, 80). */

export interface SegmentView {
  id: string;
  name: string;
  description: string | null;
  entity_type: 'CONTACT' | 'LEAD';
  filter: Record<string, unknown>;
  created_at: string;
}

export function listSegments(): Promise<{ segments: SegmentView[] }> {
  return apiRequest('/v1/marketing/segments');
}

export function createSegment(input: {
  name: string;
  description?: string | null;
  entityType: 'CONTACT' | 'LEAD';
}): Promise<SegmentView> {
  return apiRequest('/v1/marketing/segments', { method: 'POST', body: input });
}

export function deleteSegment(id: string): Promise<void> {
  return apiRequest(`/v1/marketing/segments/${id}`, { method: 'DELETE' });
}

export interface SegmentPreview {
  segment: SegmentView;
  members: number;
  sample: { email: string; name: string | null }[];
}

export function previewSegment(id: string): Promise<SegmentPreview> {
  return apiRequest(`/v1/marketing/segments/${id}/preview`);
}

export interface VariantView {
  id: string;
  label: string;
  subject: string;
  body_text: string;
  body_html: string | null;
  weight: number;
}

export interface CampaignView {
  send_window: SendWindow | null;
  id: string;
  name: string;
  from_name: string | null;
  audience_kind: 'SEGMENT' | 'LIST';
  segment_id: string | null;
  status: 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELED' | 'FAILED';
  scheduled_at: string | null;
  recipients_total: number;
  queued_count: number;
  skipped_count: number;
  failure_reason: string | null;
  finished_at: string | null;
  created_at: string;
  variants: VariantView[];
}

export function listCampaigns(): Promise<{ campaigns: CampaignView[] }> {
  return apiRequest('/v1/marketing/campaigns');
}

export interface AudienceFilter {
  statusIds?: string[];
  sourceIds?: string[];
  tagIds?: string[];
  createdFrom?: string;
  createdTo?: string;
  includeIds?: string[];
}

/** A body written as blocks, as in the page builder. The server derives both halves from it. */
export interface CampaignContent {
  blocks: Block[];
}

export function createCampaign(input: {
  name: string;
  subject: string;
  bodyText?: string;
  content?: CampaignContent;
  bodyHtml?: string | null;
  fromName?: string | null;
  segmentId?: string;
  recipients?: string[];
  filter?: AudienceFilter;
}): Promise<CampaignView> {
  return apiRequest('/v1/marketing/campaigns', { method: 'POST', body: input });
}

export interface SendWindow {
  days?: number[];
  from?: string;
  to?: string;
  until?: string;
}

/** When it may send, as opposed to when it starts: two decisions, two calls. */
export function setCampaignWindow(id: string, window: SendWindow | null): Promise<CampaignView> {
  return apiRequest(`/v1/marketing/campaigns/${id}/window`, { method: 'PUT', body: window });
}

export function saveVariant(
  id: string,
  input: { label: string; subject: string; bodyText: string; weight?: number },
): Promise<VariantView> {
  return apiRequest(`/v1/marketing/campaigns/${id}/variants`, { method: 'PUT', body: input });
}

export interface CampaignPreview {
  campaign_id: string;
  members: number;
  suppressed: number;
  without_consent: number;
  sendable: number;
}

export function previewCampaign(id: string): Promise<CampaignPreview> {
  return apiRequest(`/v1/marketing/campaigns/${id}/preview`);
}

export function scheduleCampaign(id: string, scheduledAt?: string): Promise<CampaignView> {
  return apiRequest(`/v1/marketing/campaigns/${id}/schedule`, {
    method: 'POST',
    body: scheduledAt ? { scheduledAt } : {},
  });
}

export function cancelCampaign(id: string): Promise<CampaignView> {
  return apiRequest(`/v1/marketing/campaigns/${id}/cancel`, { method: 'POST', body: {} });
}

export interface ReportFigures {
  recipients: number;
  queued: number;
  skipped: number;
  delivered: number;
  bounced: number;
  failed: number;
  opened: number;
  clicked: number;
  unsubscribed: number;
}

export interface CampaignReport {
  campaign_id: string;
  status: CampaignView['status'];
  totals: ReportFigures;
  variants: (ReportFigures & { label: string | null })[];
  links: { url: string; clicks: number }[];
  not_reported: string[];
}

export function campaignReport(id: string): Promise<CampaignReport> {
  return apiRequest(`/v1/marketing/campaigns/${id}/report`);
}
